import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';
import type {
  CreatePatientInput,
  PatientDetail,
} from '@/features/patients/types';
import type {
  Appointment,
  AppointmentDetail,
  ArrivalLookup,
  CheckedInAppointment,
  CoverCheck,
  QueueDisplay,
  ScanResult,
  QueueSummary,
  QueueTicket,
  SeeFirstFlag,
  Service,
  Vitals,
  VitalsInput,
} from './types';

/** Live data refresh until realtime push exists ("updates live"). */
const LIVE_MS = 10_000;

function useBranchKey() {
  return useBranch().selected?.id ?? 'all';
}

/** `enabled`: false skips the call (RL-1675 review: roles without queue:read). */
export function useQueue(enabled = true, date?: string) {
  const branch = useBranchKey();
  return useQuery({
    // RL-2183: `date` = a past clinic day (read-only history); omitted = today.
    queryKey: date ? ['queue', branch, date] : ['queue', branch],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ clinicDate: string; tickets: QueueTicket[] }>(
          '/queue',
          date ? { signal, query: { date } } : { signal },
        )
      ).data,
    enabled: enabled && branch !== 'all',
    refetchInterval: LIVE_MS,
  });
}

export function useQueueSummary(enabled = true) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['queue-summary', branch],
    queryFn: async ({ signal }) =>
      (await api.get<QueueSummary>('/queue/summary', { signal })).data,
    enabled: enabled && branch !== 'all',
    refetchInterval: LIVE_MS,
  });
}

export function useQueueDisplay() {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['queue-display', branch],
    queryFn: async ({ signal }) =>
      (await api.get<QueueDisplay>('/queue/display', { signal })).data,
    enabled: branch !== 'all',
    refetchInterval: 5_000,
  });
}

export function useServices() {
  return useQuery({
    queryKey: ['services'],
    queryFn: async ({ signal }) =>
      (await api.get<Service[]>('/services', { signal })).data,
    staleTime: 5 * 60_000,
  });
}

export function useDoctors() {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['doctors', branch],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ id: string; fullName: string }[]>('/doctors', {
          signal,
        })
      ).data,
    staleTime: 5 * 60_000,
  });
}

export function useAppointments(from?: string, days = 14) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['appointments', branch, from, days],
    queryFn: async ({ signal }) =>
      (
        await api.get<{
          from: string;
          days: number;
          appointments: Appointment[];
        }>('/appointments', { signal, query: { from, days } })
      ).data,
    refetchInterval: 30_000,
  });
}

export function useAppointment(id: string | null) {
  return useQuery({
    queryKey: ['appointment', id],
    queryFn: async ({ signal }) =>
      (await api.get<AppointmentDetail>(`/appointments/${id}`, { signal }))
        .data,
    enabled: !!id,
  });
}

export function useCoverPreview(ticketId: string | null) {
  return useQuery({
    queryKey: ['cover-check', ticketId],
    queryFn: async ({ signal }) =>
      (await api.get<CoverCheck>(`/queue/${ticketId}/cover-check`, { signal }))
        .data,
    enabled: !!ticketId,
    retry: false,
    staleTime: 0,
  });
}

export function useInvalidateQueue() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['queue'] }),
      qc.invalidateQueries({ queryKey: ['queue-summary'] }),
      qc.invalidateQueries({ queryKey: ['queue-display'] }),
      qc.invalidateQueries({ queryKey: ['appointments'] }),
      qc.invalidateQueries({ queryKey: ['appointment'] }),
      qc.invalidateQueries({ queryKey: ['doctor-today'] }),
      qc.invalidateQueries({ queryKey: ['calendar'] }),
      qc.invalidateQueries({ queryKey: ['waitlist'] }),
    ]);
}

export function useQueueActions() {
  const invalidate = useInvalidateQueue();
  const done = { onSuccess: () => void invalidate() };
  return {
    addWalkIn: useMutation({
      mutationFn: async (input: {
        patientId: string;
        serviceId?: string;
        payerId?: string | null;
      }) => (await api.post<QueueTicket>('/queue', input)).data,
      ...done,
    }),
    registerWalkIn: useMutation({
      mutationFn: async (
        input: CreatePatientInput & {
          serviceId: string;
          payerId?: string | null;
        },
      ) =>
        (
          await api.post<{
            existing: boolean;
            patient: PatientDetail;
            ticket: QueueTicket;
          }>('/queue/register-walk-in', input)
        ).data,
      ...done,
    }),
    scan: useMutation({
      mutationFn: async (token: string) =>
        (await api.post<ScanResult>('/queue/scan', { token })).data,
      ...done,
    }),
    checkInPatient: useMutation({
      mutationFn: async (input: {
        patientId: string;
        appointmentId?: string;
        serviceId?: string;
        payerId?: string | null;
        faceChecked: boolean;
      }) =>
        (
          await api.post<{
            result: 'checked_in' | 'walk_in';
            appointment?: CheckedInAppointment;
            ticket?: QueueTicket;
          }>('/queue/check-in', input)
        ).data,
      ...done,
    }),
    setPayer: useMutation({
      mutationFn: async ({
        id,
        payerId,
      }: {
        id: string;
        payerId: string | null;
      }) =>
        (await api.post<QueueTicket>(`/queue/${id}/payer`, { payerId })).data,
      ...done,
    }),
    saveCover: useMutation({
      /** RL-1664: `cardChecked` confirms a membership typed in the app. */
      mutationFn: async (
        v: string | { id: string; cardChecked?: boolean; memberNo?: string },
      ) => {
        const { id, ...body } = typeof v === 'string' ? { id: v } : v;
        return (await api.post<QueueTicket>(`/queue/${id}/cover-check`, body))
          .data;
      },
      ...done,
    }),
    callNext: useMutation({
      mutationFn: async (room: string) =>
        (await api.post<QueueTicket>('/queue/call-next', { room })).data,
      ...done,
    }),
    call: useMutation({
      mutationFn: async ({ id, room }: { id: string; room: string }) =>
        (await api.post<QueueTicket>(`/queue/${id}/call`, { room })).data,
      ...done,
    }),
    seeFirst: useMutation({
      mutationFn: async ({
        id,
        flag,
        note,
      }: {
        id: string;
        flag: SeeFirstFlag;
        note?: string;
      }) =>
        (await api.post<QueueTicket>(`/queue/${id}/see-first`, { flag, note }))
          .data,
      ...done,
    }),
    cancel: useMutation({
      mutationFn: async ({
        id,
        reason,
        keepalive,
      }: {
        id: string;
        reason: string;
        keepalive?: boolean;
      }) =>
        (
          await api.post<QueueTicket>(
            `/queue/${id}/cancel`,
            { reason },
            { keepalive },
          )
        ).data,
      ...done,
    }),
    // RL-1658: finish a visit that has no consultation.
    done: useMutation({
      mutationFn: async ({
        id,
        keepalive,
      }: {
        id: string;
        keepalive?: boolean;
      }) =>
        (
          await api.post<QueueTicket>(`/queue/${id}/done`, undefined, {
            keepalive,
          })
        ).data,
      ...done,
    }),
    recordVitals: useMutation({
      mutationFn: async ({ id, vitals }: { id: string; vitals: VitalsInput }) =>
        (await api.post<Vitals>(`/queue/${id}/vitals`, vitals)).data,
      ...done,
    }),
  };
}

export async function arrivalLookup(q: string) {
  return (
    await api.get<ArrivalLookup>('/queue/arrivals/lookup', { query: { q } })
  ).data;
}

export function useAppointmentActions() {
  const invalidate = useInvalidateQueue();
  const done = { onSuccess: () => void invalidate() };
  return {
    book: useMutation({
      mutationFn: async (input: {
        patientId: string;
        serviceId: string;
        doctorStaffId?: string;
        startsAt: string;
        repeat?: Appointment['repeat'];
        travelDate?: string;
        depositSen?: number;
        notes?: string;
        branchId?: string;
      }) =>
        (
          await api.post<
            Appointment & {
              series: {
                id: string;
                startsAt: string;
                movedFrom: string | null;
              }[];
              depositReason: string | null;
            }
          >(
            '/appointments',
            { ...input, branchId: undefined },
            {
              headers: input.branchId ? { 'X-Branch-Id': input.branchId } : {},
            },
          )
        ).data,
      ...done,
    }),
    reschedule: useMutation({
      mutationFn: async (v: {
        id: string;
        startsAt: string;
        doctorStaffId?: string | null;
      }) => {
        const { id, ...body } = v;
        return (
          await api.post<AppointmentDetail>(
            `/appointments/${id}/reschedule`,
            body,
          )
        ).data;
      },
      ...done,
    }),
    noShow: useMutation({
      mutationFn: async (id: string) =>
        (await api.post<AppointmentDetail>(`/appointments/${id}/no-show`)).data,
      ...done,
    }),
    depositPaid: useMutation({
      mutationFn: async (v: { id: string; method: 'cash' | 'card' }) =>
        (
          await api.post<AppointmentDetail>(`/appointments/${v.id}/deposit`, {
            method: v.method,
          })
        ).data,
      ...done,
    }),
    cancel: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
        (await api.post<Appointment>(`/appointments/${id}/cancel`, { reason }))
          .data,
      ...done,
    }),
    checkIn: useMutation({
      mutationFn: async (id: string) =>
        (await api.post<CheckedInAppointment>(`/appointments/${id}/check-in`))
          .data,
      ...done,
    }),
  };
}
